import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, RefreshCw, X } from 'lucide-react';

type BetaApplication = {
  id: string;
  name: string;
  email: string;
  reason: string | null;
  created_at: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at?: string;
  reviewed_by?: string;
};

const ApplicationsTab = () => {
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    fetchApplications();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('beta_applications_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'beta_applications' 
        }, 
        () => {
          fetchApplications();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      
      console.log('Fetching beta applications...');
      
      const { data: applications, error: applicationsError } = await supabase
        .from('beta_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (applicationsError) {
        console.error('Error fetching applications:', applicationsError);
        throw applicationsError;
      }

      console.log('Fetched applications:', applications?.length || 0);
      
      const typedData = applications?.map(app => ({
        ...app,
        status: app.status as 'pending' | 'approved' | 'rejected'
      })) || [];
      
      setApplications(typedData);
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      setLoadError(error.message || 'Failed to load beta applications');
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Beta-Bewerbungen: ' + (error.message || 'Unbekannter Fehler'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const approveApplication = async (id: string) => {
    const { data, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    
    const userId = data?.user?.id;
    if (!userId) {
      toast({
        title: 'Fehler',
        description: 'Benutzer nicht gefunden.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsApproving(id);
    
    try {
      const { data: application, error: applicationError } = await supabase
        .from('beta_applications')
        .select('name, email')
        .eq('id', id)
        .single();

      if (applicationError) throw applicationError;

      // Call the RPC helper to approve the application and generate a code
      const { data: responseData, error } = await supabase.functions.invoke('rpc-helpers', {
        body: { 
          action: 'approve_application',
          data: { 
            application_id: id, 
            approver_id: userId
          }
        }
      });

      if (error) throw error;

      toast({
        title: 'Bewerbung genehmigt',
        description: `Einladungscode wurde generiert und per E-Mail versendet: ${responseData.code}`,
      });
      
      fetchApplications();
      
    } catch (error: any) {
      console.error('Error approving application:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler bei der Genehmigung der Bewerbung: ' + (error.message || 'Unbekannter Fehler'),
        variant: 'destructive',
      });
    } finally {
      setIsApproving(null);
    }
  };

  const rejectApplication = async (id: string) => {
    const { data } = await supabase.auth.getUser();
    const userId = data?.user?.id;
    setIsRejecting(id);
    
    try {
      const { error } = await supabase
        .from('beta_applications')
        .update({ 
          status: 'rejected' as const, 
          reviewed_at: new Date().toISOString(), 
          reviewed_by: userId 
        })
        .eq('id', id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Bewerbung abgelehnt',
        description: 'Die Bewerbung wurde abgelehnt.',
      });
      
      fetchApplications();
      
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler bei der Ablehnung der Bewerbung: ' + (error.message || 'Unbekannter Fehler'),
        variant: 'destructive',
      });
    } finally {
      setIsRejecting(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">Genehmigt</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">Abgelehnt</Badge>;
      default:
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">Ausstehend</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-row justify-between items-center">
          <div>
            <CardTitle>Beta-Bewerbungen</CardTitle>
            <CardDescription>
              Verwalten Sie eingehende Bewerbungen für den Beta-Zugang.
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchApplications}
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Aktualisieren
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-jade" />
            <p>Bewerbungen werden geladen...</p>
          </div>
        ) : loadError ? (
          <Alert variant="destructive">
            <AlertDescription>
              Fehler beim Laden der Bewerbungen: {loadError}
            </AlertDescription>
          </Alert>
        ) : applications.length === 0 ? (
          <Alert>
            <AlertDescription>
              Derzeit sind keine Beta-Bewerbungen vorhanden.
            </AlertDescription>
          </Alert>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Grund</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.map((app) => (
                <TableRow key={app.id}>
                  <TableCell className="font-medium">{app.name}</TableCell>
                  <TableCell>{app.email}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {app.reason || <em className="text-gray-500">Keine Angabe</em>}
                  </TableCell>
                  <TableCell>{formatDate(app.created_at)}</TableCell>
                  <TableCell>{getStatusBadge(app.status)}</TableCell>
                  <TableCell className="space-x-2">
                    {app.status === 'pending' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => approveApplication(app.id)}
                          disabled={isApproving === app.id}
                          className="bg-green-500/10 hover:bg-green-500/20 border-green-500/30"
                        >
                          {isApproving === app.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                          <span className="ml-2">Genehmigen</span>
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => rejectApplication(app.id)}
                          disabled={isRejecting === app.id}
                          className="bg-red-500/10 hover:bg-red-500/20 border-red-500/30"
                        >
                          {isRejecting === app.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                          <span className="ml-2">Ablehnen</span>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default ApplicationsTab;
